/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatSortModule } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule, MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { MatNativeDateModule } from '@angular/material/core';
import { ModalHeaderModule } from '@sondermind/features/modal-header';
import { SondermindCalendarModule } from '@sondermind/features/modules/calendar';
import { SondermindClientsModule } from '@sondermind/features/modules/clients';
import { SondermindCommonModule } from '@sondermind/features/modules/common';
import { NavbarModule } from '@sondermind/features/navbar';
import { ClientSearchBarComponent } from '@sondermind/features/provider-facing/client-search-bar';
import {
  ClinicalAssessmentSendModalComponent,
  ClinicalQuestionnaireScoreDetailModalComponent,
  ClinicalQuestionnaireScoringComponent,
  ClinicalQuestionnaireSelectModalComponent,
  ClinicalQuestionnaireSummaryComponent,
  ClinicalQuestionnairesOverviewComponent
} from '@sondermind/features/provider-facing/clinical-questionnaires';

import { FormlibModule } from '@sondermind/formlib';

import { IrisButtonModule } from '@sondermind/iris/button';
import { IrisCardModule } from '@sondermind/iris/card';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisIconComponent } from '@sondermind/iris/icon';
import { IrisIconButtonModule } from '@sondermind/iris/icon-button';
import { IrisRadioGroupModule } from '@sondermind/iris/radio-group';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';

import { DirectiveModule } from '@sondermind/utilities/directives';
import { DateTZPipe, PipesModule, ReferralSourcePipe } from '@sondermind/utilities/pipes';
import { LocalizedDatePipe } from '@sondermind/utilities/time-and-date';
import { RequestedTimeSlotPipe } from '@sondermind/utilities/pipes/requested-time-slot.pipe';

import { TooltipModule } from '@sondermind/features/tooltip';
import { IrisMenuModule } from '@sondermind/iris/menu';
import { IrisChipModule } from '@sondermind/iris/chip';
import { RcopiaModalComponent } from '@sondermind/features/modals/rcopia';
import { defaultSnackBar } from '@sondermind/material';
import { IrisTabModule } from '@sondermind/iris/tab';
import { IrisDropdownModule } from '@sondermind/iris/dropdown';
import { IrisTextInputModule } from '@sondermind/iris/text-input';
import { IrisModalRef } from '@sondermind/iris/modal';

import { IrisTextAreaModule } from '@sondermind/iris/text-area';
import { DocSendModal } from '@sondermind/features/provider-facing/therapist-documents';
import {
  ChartRiskFlagBannerComponent,
  ClientCQAnalyticsCardComponent,
  ClientDetailAssessmentsPage,
  ClientDetailJourneyPage
} from '@sondermind/features/provider-facing/client-detail';
import { BulkClientUploadDialogComponent } from '@sondermind/features/embedded-client';

import { IrisSpinnerComponent } from '@sondermind/iris/spinner';
import { IrisTooltipComponent } from '@sondermind/iris/tooltip';
import { CoreModule as SondermindCoreModule } from '@sondermind/core/core.module';
import { IrisCardDeprecatedModule } from '@sondermind/core/design/iris-card-deprecated/iris-card-deprecated.module';
import { SonderModalModule } from '@sondermind/core/design/sonder-modal/sonder-modal.module';
import { DesignModule } from '@sondermind/core/design.module';

import { InlineNotificationComponent } from '@sondermind/member/components/inline-notification/inline-notification.component';
import { ClientPrescriptionsInfoPage } from '@sondermind/member/pages/client-detail-prescriptions-info/client-detail-prescriptions-info.component';

import { ClientNotesContainerComponent } from '@sondermind/member/pages/client-notes-container/client-notes-container.component';
import { ProviderNotAcceptingClientsModalComponent } from '@sondermind/member/pages/clients-list/provider-not-accepting-clients-modal/provider-not-accepting-clients-modal.component';
import { CurrentClientsListPage } from '@sondermind/member/pages/current-clients-list/current-clients-list.component';
import { MatchClientsTable } from '@sondermind/member/pages/match-clients-list/match-clients-table/match-clients-table.component';
import { PsychiatryModalModule } from '@sondermind/member/pages/psychiatry/psychiatry.module';

import { ClientTerminationReasonsModalComponent } from '../../components/client-termination-reasons-modal/client-termination-reasons-modal.component';
import { ProBonoModalComponent } from '../../components/pro-bono-modal/pro-bono-modal.component';
import { ClientDetailPage } from '../../pages/client-detail/client-detail.component';
import { ClientDetailAssessmentTypePage } from '../../pages/client-detail-assessment-type/client-detail-assessment-type.component';
import { ClientDetailContactInfoPage } from '../../pages/client-detail-contact-info/client-detail-contact-info.component';
import { ClientDetailDocumentsPage } from '../../pages/client-detail-documents/client-detail-documents.component';
import { ClientDetailPaymentInfoPage } from '../../pages/client-detail-payment-info/client-detail-payment-info.component';
import { VaAuthorizationsCardComponent } from '../../pages/client-detail-payment-info/va-authorizations-card/va-authorizations-card.component';
import { MatchClientsListPage } from '../../pages/match-clients-list/match-clients-list.component';
import { ProviderNetworkNamePipe } from '../../pipes/provider-network-name.pipe';
import { ActiveFirstTabDirective } from '../../pages/match-clients-list/active-first-tab.directive';

import { ClientsAppComponent } from './clients-app.component';
import { ClientsRoutingModule } from './clients-routing.module';
/* eslint-enable max-len */

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    DesignModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMaskDirective,

    // Iris modules
    IrisButtonModule,
    IrisCardModule,
    IrisCardDeprecatedModule,
    IrisFormFieldModule,
    IrisIconComponent,
    IrisIconButtonModule,
    IrisRadioGroupModule,
    IrisCheckboxModule,
    IrisTabModule,
    IrisDropdownModule,
    IrisTextInputModule,
    IrisTextAreaModule,
    IrisMenuModule,
    IrisChipModule,
    IrisSpinnerComponent,
    IrisTooltipComponent,

    // Material modules
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    MatDialogModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSelectModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,

    // Sondermind modules
    SonderModalModule,
    SondermindCommonModule,
    SondermindCoreModule,
    SondermindCalendarModule,
    SondermindClientsModule,
    ClientsRoutingModule,
    NavbarModule,
    DirectiveModule,
    FormlibModule,
    PipesModule,
    DateTZPipe,
    ModalHeaderModule,
    PsychiatryModalModule,
    TooltipModule,

    // Standalone components and pipes
    RcopiaModalComponent,
    RequestedTimeSlotPipe,
    ReferralSourcePipe,
    LocalizedDatePipe,
    ProviderNetworkNamePipe,
    ClientDetailAssessmentsPage,
    ClientDetailJourneyPage,
    ChartRiskFlagBannerComponent,
    ClientCQAnalyticsCardComponent,
    ClientSearchBarComponent,
    ClinicalAssessmentSendModalComponent,
    ClinicalQuestionnaireScoreDetailModalComponent,
    ClinicalQuestionnaireScoringComponent,
    ClinicalQuestionnaireSelectModalComponent,
    ClinicalQuestionnaireSummaryComponent,
    ClinicalQuestionnairesOverviewComponent,
    DocSendModal,
    MatchClientsTable,
    ClientDetailDocumentsPage,
    BulkClientUploadDialogComponent,
    VaAuthorizationsCardComponent,
  ],
  declarations: [
    ClientsAppComponent,
    MatchClientsListPage,
    ClientDetailPage,
    ClientNotesContainerComponent,
    ClientDetailPaymentInfoPage,
    ClientDetailContactInfoPage,
    ClientDetailAssessmentTypePage,
    CurrentClientsListPage,
    ClientPrescriptionsInfoPage,
    ProBonoModalComponent,
    ClientTerminationReasonsModalComponent,
    ProviderNotAcceptingClientsModalComponent,
    InlineNotificationComponent,
    ActiveFirstTabDirective
  ],
  providers: [
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useFactory: defaultSnackBar },
    { provide: MAT_DIALOG_DATA, useValue: {} },
    { provide: IrisModalRef, useValue: {} },
    provideNgxMask()
  ]
})
export class ClientsModule { }
